import { Injectable } from '@angular/core'
import { HttpParams, HttpClient, HttpResponse } from '@angular/common/http'
import { map, timeout } from 'rxjs/operators'
import { Observable } from 'rxjs'

import { environment } from '../../../environments/environment'
import { ConnectivityService } from './offline/connectivity.service'

// Timeout for API calls — prevents requests from hanging indefinitely
// when WebView reports online but device is actually offline.
// 30s accommodates heavier endpoints under prod data volumes.
const HTTP_TIMEOUT = 30000

@Injectable({
  providedIn: 'root'
})
export class ResourceService {

  get isOnline() {
    return this.connectivityService.isOnline
  }

  constructor(
    private http: HttpClient,
    private connectivityService: ConnectivityService
  ) {}

  list(resourceName: string, params?: HttpParams): Observable<any> {
    return this.http
      .get(`${environment.apiBaseUrl}${resourceName}`, {
        params
      })
      .pipe(timeout(HTTP_TIMEOUT), map((res) => res))
  }

  show(
    resourceName: string,
    id: number | string,
    suffix?: string
  ): Observable<any> {
    return this.http
      .get(
        `${environment.apiBaseUrl}${resourceName}/${id}` +
          (suffix ? `/${suffix}` : '')
      )
      .pipe(
        timeout(HTTP_TIMEOUT),
        map((res) => {
          return res
        })
      )
  }

  store(resourceName: string, formData: FormData): Observable<any> {
    return this.http
      .post(`${environment.apiBaseUrl}${resourceName}`, formData)
      .pipe(
        map((response) => {
          return response
        })
      )
  }

  update(
    resourceName: string,
    id: number | string,
    formData: FormData
  ): Observable<any> {
    return this.http
      .put(`${environment.apiBaseUrl}${resourceName}/${id}`, formData)
      .pipe(
        map((res) => {
          return res
        })
      )
  }

  patch(
    resourceName: string,
    id: number | string,
    suffix: string,
    formData: FormData
  ): Observable<any> {
    return this.http
      .patch(
        `${environment.apiBaseUrl}${resourceName}/${id}/${suffix}`,
        formData
      )
      .pipe(
        map((res) => {
          return res
        })
      )
  }

  // Fetches the resource as a binary blob and triggers a browser save-as dialog.
  // Filename is taken from Content-Disposition when present, else `fallbackName`.
  // No query-string timeout here : exports can be long (big ZIPs).
  download(
    resourceName: string,
    params: HttpParams,
    fallbackName: string
  ): Observable<void> {
    return this.http
      .get(`${environment.apiBaseUrl}${resourceName}`, {
        params,
        responseType: 'blob',
        observe: 'response'
      })
      .pipe(
        map((res: HttpResponse<Blob>) => {
          const filename = this.parseContentDispositionFilename(
            res.headers.get('Content-Disposition')
          ) || fallbackName
          this.triggerBrowserDownload(res.body, filename)
        })
      )
  }

  private parseContentDispositionFilename(header: string | null): string | null {
    if (!header) return null
    // RFC 5987 first, then quoted or bare filename=
    const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header)
    if (utf8) return decodeURIComponent(utf8[1])
    const quoted = /filename="([^"]+)"/i.exec(header)
    if (quoted) return quoted[1]
    const bare = /filename=([^;]+)/i.exec(header)
    return bare ? bare[1].trim() : null
  }

  private triggerBrowserDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    // Release the object URL after the click has been processed
    setTimeout(() => URL.revokeObjectURL(url), 0)
  }

  delete(resourceName: string, id: number | string): Observable<any> {
    return this.http
      .delete(`${environment.apiBaseUrl}${resourceName}/${id}`)
      .pipe(
        map((res) => {
          return res
        })
      )
  }
}
